import React, { useState, useEffect } from 'react';
import {
  Typography, Paper, Table, TableBody, Box, TableCell, TableContainer, TableHead, TableRow, Checkbox, Button, ButtonGroup, Grid, TextField, MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Snackbar, Alert } from "@mui/material";
import TablePagination from '@mui/material/TablePagination';
import CircularProgress from '@mui/material/CircularProgress';

const CreateNewUser = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // success, error, info, warning
  const [selectThousand, setSelectThousand] = useState(false);   // state for select 1000 students checkbox
  const [loading, setLoading] = useState(false); // Add loading state
  const [tableLoading, setTableLoading] = useState(false);
// Add pagination states
const [page, setPage] = useState(0);
const rowsPerPage = 15; // Fixed to 15 students per page
const [filteredStudents, setFilteredStudents] = useState([]); // Separate state for filtered students

// Define paginatedStudents
const paginatedStudents = filteredStudents.slice(
  page * rowsPerPage,
  page * rowsPerPage + rowsPerPage
);

// Update handleSearchChange to manage filtered students and reset page
const handleSearchChange = (event) => {
  const searchValue = event.target.value.toLowerCase();
  setSearch(searchValue);
  setPage(0); // Reset to first page on search

  // Filter students based on search input (name or username)
  const filtered = students.filter(
    (student) =>
      student.name.toLowerCase().includes(searchValue) ||
      student.username.toLowerCase().includes(searchValue)
  );
  setFilteredStudents(filtered);
};

// Update useEffect to initialize filteredStudents and reset on search clear
useEffect(() => {
  if (search === '') {
    fetchStudents(); // Reset to full student list when search is empty
    setFilteredStudents(students); // Initialize filteredStudents with full list
  }
}, [search, classId, sectionId]);

// Initialize filteredStudents after fetching students
useEffect(() => {
  fetchClasses(); // Fetch classes on component mount
  fetchStudents();
}, []);

useEffect(() => {
  fetchStudents(); // Fetch students when classId or sectionId changes
}, [classId, sectionId]);

  // Fetch Classes
  const fetchClasses = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") {
        throw new Error("Record Not Found");
      }
      setClasses(data);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  // Fetch Sections
  const fetchSections = async (classId) => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Teacher/ddlSection_clsId`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify({
            teacherId: sessionStorage.getItem("employeeId"),
            classId,
          }),
        }
      );
      const data = await response.json();
      setSections(data);
    } catch (error) {
      console.error("Error fetching sections:", error);
    }
  };

  // Fetch Students
const fetchStudents = async () => {
  try {
    setTableLoading(true); // Start table loading
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const response = await fetch(`${apiUrl}/StudentUser/GetStudentDetails_ToCreate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: sessionStorage.getItem("token"),
      },
      body: JSON.stringify({
        classId: classId || 0,
        sectionId: sectionId || 0,
      }),
    });
    const data = await response.json();

    // Map data to include admissionNo and mobileNo
    const formattedData = data.map((student) => ({
      id: student.userID, // Unique ID for selection
      name: student.studentName,
      username: student.userName || "Not Assigned",
      password: student.password || "Not Set",
      admissionNo: student.admissionNo, // Include admissionNo
      mobileNo: student.mobileNo, // Include mobileNo
    }));

    setStudents(formattedData);
    setFilteredStudents(formattedData); // Initialize filteredStudents
  } catch (error) {
    console.error("Error fetching students:", error);
  } finally {
    setTableLoading(false); // Stop table loading
  }
};

// Update handleSelectAll to work with paginated students
const handleSelectAll = () => {
  const currentPageIds = paginatedStudents.map((student) => student.id);
  const allSelectedOnPage = currentPageIds.every((id) => selected.includes(id));

  if (allSelectedOnPage) {
    // If all students on the current page are selected, deselect them
    setSelected((prev) => prev.filter((id) => !currentPageIds.includes(id)));
  } else {
    // Select all students on the current page, keeping existing selections
    setSelected((prev) => [
      ...new Set([...prev, ...currentPageIds]), // Use Set to avoid duplicates
    ]);
  }
};

// Handle page change
const handleChangePage = (event, newPage) => {
  setPage(newPage);
};

 // Update handleCheckboxClick to toggle selection and deselect others
const handleCheckboxClick = (id) => {
  setSelected((prev) =>
    prev.includes(id)
      ? prev.filter((item) => item !== id) // Remove the clicked student from selection
      : [...prev, id] // Add the clicked student to selection
  );
};

// Handler for select 1000 students checkbox
const handleSelectThousand = () => {
  if (selectThousand) {
    // Deselect all students if checkbox is unchecked
    setSelected([]);
  } else {
    // Select up to 1000 students
    const selectedIds = students.slice(0, 1000).map((student) => student.id);
    setSelected(selectedIds);
  }
  setSelectThousand(!selectThousand);
};

  const handleClassChange = (event) => {
    const selectedClassId = event.target.value;
    setClassId(selectedClassId);
    setSectionId(""); // Reset section when class changes
    setStudents([]); // Reset students when class changes
    fetchSections(selectedClassId); // Fetch sections based on selected class ID
  };

  const handleSectionChange = (event) => {
    setSectionId(event.target.value);
  };

const handleSave = async () => {
  try {
    if (selected.length === 0) {
      setSnackbarMessage("Please select at least one student.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    setTableLoading(true); // Start table loading
    // Prepare payload
    const payload = selected.map((studentId) => {
      const student = students.find((s) => s.id === studentId);
      return {
        admissionNo: student.admissionNo, // Include admissionNo
        password: student.mobileNo, // Include mobileNo
        studentId: student.id,
      };
    });

    // API call
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const response = await fetch(`${apiUrl}/StudentUser/InsertStudentUsers`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: sessionStorage.getItem("token"),
      },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      setSnackbarMessage("Users saved successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setSelected([]); // Clear selection after saving
      await fetchStudents(); // Refresh the student list
    } else {
      const errorData = await response.json();
      console.error("Error saving users:", errorData);
      setSnackbarMessage("Failed to save users. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  } catch (error) {
    console.error("Error:", error);
    setSnackbarMessage("An error occurred while saving users.");
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  } finally {
    setTableLoading(false); // Stop table loading
  }
};
    

  useEffect(() => {
    fetchClasses(); // Fetch classes on component mount
    fetchStudents();
  }, []);

  useEffect(() => {
    fetchStudents(); // Fetch students when classId or sectionId changes
  }, [classId, sectionId]);

  return (
    <div style={{ padding: '20px' }}>
 <Box
          sx={{
            backgroundColor: '#0B1F40', // dark navy blue
            padding: '12px 24px',
            boxShadow: 3,
            borderRadius: '4px 4px 0 0',
            marginBottom: '15px',
           width: "100%",
          }}
        >
          <Typography variant="h4" sx={{ color: '#fff',  fontWeight: 'bold' }}>
        Create New User
      </Typography> </Box>     
       <div style={{ display: 'flex', marginBottom: '20px' }}>
         <TextField
                  label="Search"
                  variant="outlined"
                  size="small"
                  value={search}
                  onChange={handleSearchChange}
                  style={{ marginRight: '20px' }}
                />
        <ButtonGroup>
          <Button variant="outlined" color="primary" onClick={() => navigate('/studentuser')}>
            Created User
          </Button>
          <Button variant="contained" color="primary" onClick={() => navigate('/create-new-user')}>
            Create New User
          </Button>
        </ButtonGroup>
      </div>

  <Paper elevation={3} style={{ padding: '20px', marginBottom: '20px' }}>
  <Grid container spacing={2}>
    <Grid item xs={12} sm={6}>
      <TextField
        select
        label="Class"
        value={classId}
        onChange={handleClassChange}
        fullWidth
      >
        {classes.length > 0 ? (
          classes.map((cls) => (
            <MenuItem key={cls.classId} value={cls.classId}>
              {cls.className}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No Classes Available</MenuItem>
        )}
      </TextField>
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        select
        label="Section"
        value={sectionId}
        onChange={handleSectionChange}
        fullWidth
        disabled={sections.length === 0}
      >
        {sections.length > 0 ? (
          sections.map((section) => (
            <MenuItem key={section.sectionId} value={section.sectionId}>
              {section.sectionName}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No Sections Available</MenuItem>
        )}
      </TextField>
    </Grid>
  </Grid>

  <TableContainer sx={{ marginTop: 3 }}>
    {tableLoading ? (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
        <CircularProgress />
      </div>
    ) : (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" colSpan={4}>
              <Checkbox
                checked={selectThousand}
                onChange={handleSelectThousand}
                inputProps={{ 'aria-label': 'Select 1000 Students' }}
              />
              Select 1000 Students
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={
                  selected.length > 0 &&
                  paginatedStudents.some((student) => selected.includes(student.id)) &&
                  !paginatedStudents.every((student) => selected.includes(student.id))
                }
                checked={
                  paginatedStudents.length > 0 &&
                  paginatedStudents.every((student) => selected.includes(student.id))
                }
                onChange={handleSelectAll}
              />
            </TableCell>
            <TableCell
              style={{ fontWeight: 'bold', cursor: 'pointer' }}
              onClick={handleSelectAll}
            >
              Student Name ({selected.length} selected)
            </TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>User Name</TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>Password</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedStudents.length > 0 ? (
            paginatedStudents.map((student) => (
              <TableRow key={student.id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.includes(student.id)}
                    onChange={() => handleCheckboxClick(student.id)}
                  />
                </TableCell>
                <TableCell onClick={() => handleCheckboxClick(student.id)} style={{ cursor: 'pointer' }}>
                  {student.name}
                </TableCell>
                <TableCell>{student.username}</TableCell>
                <TableCell>{student.password}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} align="center">
                No students available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    )}
  </TableContainer>
  {!tableLoading && (
    <TablePagination
      rowsPerPageOptions={[15]} // Fixed to 15
      component="div"
      count={filteredStudents.length} // Total number of filtered students
      rowsPerPage={rowsPerPage}
      page={page}
      onPageChange={handleChangePage}
    />
  )}
</Paper>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
      <Button variant="contained" color="primary" onClick={handleSave}>
  Save
</Button>
        <Button variant="outlined" onClick={() => navigate(-1)} color="secondary">Cancel</Button>
      </div>
      <Snackbar
  open={snackbarOpen}
  autoHideDuration={6000}
  onClose={() => setSnackbarOpen(false)}
  anchorOrigin={{ vertical: "top", horizontal: "center" }}
>
  <Alert 
    onClose={() => setSnackbarOpen(false)} 
    severity={snackbarSeverity} 
    sx={{ width: "100%" }}
  >
    {snackbarMessage}
  </Alert>
</Snackbar>
    </div>
  );
};

export default CreateNewUser;
